import React, { ReactElement, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import "./fade-between-container.scss";

export const FadeBetweenContainer = ({
  children,
  className,
  options,
}: {
  children: ReactElement;
  className?: string;
  options?: {
    fadeDuration?: number;
    timeInBetween?: number;
  };
}) => {
  const { fadeDuration = 500, timeInBetween = 400 } = options ?? {};

  const [displayChild, setDisplayChild] = useState<React.ReactNode>(children);
  const [fadeState, setFadeState] = useState<"showing" | "fading-out">("showing");

  const prevChildrenRef = useRef(children);
  const timeouts = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      timeouts.current.forEach((id) => clearTimeout(id));
      timeouts.current = [];
    };
  }, []);

  useEffect(() => {
    if (prevChildrenRef.current === children) return;
    // Start fading out
    setFadeState("fading-out");

    // After fade out completes, update the content
    const fadeOutTimeout = window.setTimeout(() => {
      setDisplayChild(children);
      
      // After timeInBetween, start fading back in
      const fadeInTimeout = window.setTimeout(() => {
        setFadeState("showing");
      }, timeInBetween);
      
      timeouts.current.push(fadeInTimeout);
    }, fadeDuration);

    timeouts.current.push(fadeOutTimeout);
    prevChildrenRef.current = children;
  }, [children.key, fadeDuration, timeInBetween]);

  useEffect(() => {
    if (fadeState !== "showing" || prevChildrenRef.current === children) return;
    setDisplayChild(children);
  }, [children]);

  return (
    <div
      className={classNames("fade-out-container", className, {
        "fading-out": fadeState === "fading-out",
      })}
      style={{
        transitionDuration: `${fadeDuration}ms`,
      }}
    >
      {displayChild}
    </div>
  );
};
